export type MotionPreference = 'system' | 'reduced' | 'full';

export interface PlayerData {
  version: 1;
  muted: boolean;
  motion: MotionPreference;
  haptics: boolean;
  onboardingComplete: boolean;
  bestTotal: number;
  bestStages: Record<string, number>;
}

export const STORAGE_KEY = 'surprisebliss.kinetic-bloom.v1';

export const DEFAULT_PLAYER_DATA: PlayerData = {
  version: 1,
  muted: false,
  motion: 'system',
  haptics: true,
  onboardingComplete: false,
  bestTotal: 0,
  bestStages: {},
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function parsePlayerData(raw: string | null): PlayerData {
  if (!raw) return { ...DEFAULT_PLAYER_DATA, bestStages: {} };
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || value.version !== 1) return { ...DEFAULT_PLAYER_DATA, bestStages: {} };
    const motion = value.motion;
    const stages = value.bestStages;
    if (
      typeof value.muted !== 'boolean' ||
      (motion !== 'system' && motion !== 'reduced' && motion !== 'full') ||
      typeof value.haptics !== 'boolean' ||
      typeof value.onboardingComplete !== 'boolean' ||
      typeof value.bestTotal !== 'number' ||
      !Number.isFinite(value.bestTotal) ||
      value.bestTotal < 0 ||
      !isRecord(stages)
    ) return { ...DEFAULT_PLAYER_DATA, bestStages: {} };

    const bestStages: Record<string, number> = {};
    for (const [key, score] of Object.entries(stages)) {
      if (typeof score === 'number' && Number.isFinite(score) && score >= 0) bestStages[key] = Math.round(score);
    }
    return {
      version: 1,
      muted: value.muted,
      motion,
      haptics: value.haptics,
      onboardingComplete: value.onboardingComplete,
      bestTotal: Math.round(value.bestTotal),
      bestStages,
    };
  } catch {
    return { ...DEFAULT_PLAYER_DATA, bestStages: {} };
  }
}

export function loadPlayerData(storage: Pick<Storage, 'getItem'> | null = safeStorage()): PlayerData {
  if (!storage) return { ...DEFAULT_PLAYER_DATA, bestStages: {} };
  try {
    return parsePlayerData(storage.getItem(STORAGE_KEY));
  } catch {
    return { ...DEFAULT_PLAYER_DATA, bestStages: {} };
  }
}

export function savePlayerData(data: PlayerData, storage: Pick<Storage, 'setItem'> | null = safeStorage()): boolean {
  if (!storage) return false;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(data));
    return true;
  } catch {
    return false;
  }
}

export function updateBest(data: PlayerData, stageId: string, stageScore: number, total?: number): { data: PlayerData; stageBest: boolean; totalBest: boolean } {
  const previousStage = data.bestStages[stageId] ?? 0;
  const stageBest = stageScore > previousStage;
  const totalBest = total !== undefined && total > data.bestTotal;
  return {
    stageBest,
    totalBest,
    data: {
      ...data,
      bestStages: stageBest ? { ...data.bestStages, [stageId]: stageScore } : { ...data.bestStages },
      bestTotal: totalBest && total !== undefined ? total : data.bestTotal,
    },
  };
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function prefersReducedMotion(data: PlayerData): boolean {
  if (data.motion === 'reduced') return true;
  if (data.motion === 'full') return false;
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
